import { useEffect, useRef, useState } from 'react';
import './GraphViewPage.css';

const GraphViewPage = ({ documents, onNavigate }) => {
  const canvasRef = useRef(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);

  useEffect(() => {
    // Build graph from documents
    const allDocs = [
      ...documents.analyses.map(d => ({ ...d, category: 'analysis' })),
      ...documents.screenplays.map(d => ({ ...d, category: 'screenplay' })),
      ...documents.notes.map(d => ({ ...d, category: 'note' })),
      ...documents.essays.map(d => ({ ...d, category: 'essay' }))
    ];

    // Create nodes
    const graphNodes = allDocs.map((doc, i) => ({
      id: doc.id,
      name: doc.name.replace(/\.(md|markdown|html|htm|fountain)$/, ''),
      category: doc.category,
      doc: doc,
      x: Math.random() * 800,
      y: Math.random() * 600,
      vx: 0,
      vy: 0
    }));

    // Extract links from content (simplified - would need to read files)
    const graphEdges = [];
    
    // Add some example connections based on shared tags or proximity
    allDocs.forEach((doc, i) => {
      allDocs.forEach((other, j) => {
        if (i !== j && Math.random() > 0.7) {
          graphEdges.push({
            source: doc.id,
            target: other.id
          });
        }
      });
    });

    setNodes(graphNodes);
    setEdges(graphEdges);
  }, [documents]);

  useEffect(() => {
    if (!canvasRef.current || nodes.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width = canvas.offsetWidth * window.devicePixelRatio;
    const height = canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    const displayWidth = canvas.offsetWidth;
    const displayHeight = canvas.offsetHeight;

    // Force simulation
    const simulation = () => {
      // Apply forces
      nodes.forEach(node => {
        // Center force
        const centerX = displayWidth / 2;
        const centerY = displayHeight / 2;
        node.vx += (centerX - node.x) * 0.001;
        node.vy += (centerY - node.y) * 0.001;

        // Repulsion between nodes
        nodes.forEach(other => {
          if (node.id === other.id) return;
          const dx = other.x - node.x;
          const dy = other.y - node.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const force = 50 / (dist * dist);
          node.vx -= (dx / dist) * force;
          node.vy -= (dy / dist) * force;
        });

        // Link attraction
        edges.forEach(edge => {
          if (edge.source === node.id) {
            const target = nodes.find(n => n.id === edge.target);
            if (target) {
              const dx = target.x - node.x;
              const dy = target.y - node.y;
              node.vx += dx * 0.01;
              node.vy += dy * 0.01;
            }
          }
        });

        // Damping
        node.vx *= 0.9;
        node.vy *= 0.9;

        // Update position
        node.x += node.vx;
        node.y += node.vy;

        // Boundary
        node.x = Math.max(50, Math.min(displayWidth - 50, node.x));
        node.y = Math.max(50, Math.min(displayHeight - 50, node.y));
      });
    };

    // Render
    const render = () => {
      ctx.clearRect(0, 0, displayWidth, displayHeight);

      // Draw edges
      ctx.strokeStyle = '#2a2a2a';
      ctx.lineWidth = 1;
      edges.forEach(edge => {
        const source = nodes.find(n => n.id === edge.source);
        const target = nodes.find(n => n.id === edge.target);
        if (source && target) {
          ctx.beginPath();
          ctx.moveTo(source.x, source.y);
          ctx.lineTo(target.x, target.y);
          ctx.stroke();
        }
      });

      // Draw nodes
      nodes.forEach(node => {
        const isHovered = hoveredNode === node.id;
        const isSelected = selectedNode === node.id;
        
        // Node color by category
        const colors = {
          analysis: '#d4af37',
          screenplay: '#8b7355',
          note: '#a0a0a0',
          essay: '#6a6a6a'
        };
        
        const color = colors[node.category] || '#a0a0a0';
        const radius = isHovered || isSelected ? 8 : 6;

        // Glow effect for hovered/selected
        if (isHovered || isSelected) {
          ctx.shadowBlur = 15;
          ctx.shadowColor = color;
        } else {
          ctx.shadowBlur = 0;
        }

        // Draw node
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(node.x, node.y, radius, 0, Math.PI * 2);
        ctx.fill();

        ctx.shadowBlur = 0;

        // Draw label for hovered or selected nodes
        if (isHovered || isSelected) {
          ctx.fillStyle = '#e8e8e8';
          ctx.font = '12px "Instrument Sans", sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(node.name, node.x, node.y - 15);
        }
      });
    };

    // Animation loop
    let animationId;
    const animate = () => {
      simulation();
      render();
      animationId = requestAnimationFrame(animate);
    };
    animate();

    // Mouse interaction
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      let found = null;
      nodes.forEach(node => {
        const dist = Math.sqrt((node.x - x) ** 2 + (node.y - y) ** 2);
        if (dist < 10) {
          found = node.id;
        }
      });

      setHoveredNode(found);
      canvas.style.cursor = found ? 'pointer' : 'default';
    };

    const handleClick = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      nodes.forEach(node => {
        const dist = Math.sqrt((node.x - x) ** 2 + (node.y - y) ** 2);
        if (dist < 10) {
          setSelectedNode(node.id);
          onNavigate(node.doc);
        }
      });
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('click', handleClick);

    return () => {
      cancelAnimationFrame(animationId);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('click', handleClick);
    };
  }, [nodes, edges, hoveredNode, selectedNode, onNavigate]);

  return (
    <div className="graph-view-page">
      <div className="page-header">
        <h1 className="page-title">Knowledge Graph</h1>
        <p className="page-subtitle">
          Visualize connections between films, analyses, and ideas
        </p>
      </div>

      <div className="graph-container">
        <canvas ref={canvasRef} className="graph-canvas" />
        
        {nodes.length === 0 && (
          <div className="graph-empty">
            <div className="empty-state-icon">◬</div>
            <div className="empty-state-text">No documents to visualize</div>
            <div className="empty-state-hint">Add some analyses or notes to see connections</div>
          </div>
        )}
      </div>

      <div className="graph-legend">
        <div className="legend-title">Categories</div>
        <div className="legend-items">
          <div className="legend-item">
            <span className="legend-color" style={{ background: '#d4af37' }}></span>
            <span className="legend-label">Film Analysis</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ background: '#8b7355' }}></span>
            <span className="legend-label">Screenplays</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ background: '#a0a0a0' }}></span>
            <span className="legend-label">Research Notes</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ background: '#6a6a6a' }}></span>
            <span className="legend-label">Visual Essays</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GraphViewPage;
